// The one country catalog of the web app: every ISO 3166-1 alpha-2 code.
//
// It is the whole list on purpose. The server takes any two-letter code in a
// rule (CampaignService.validateRules: ^[A-Z]{2}$), the edge compares it with
// the visitor's country from Cloudflare as is, and internal/geo knows a zone
// for every code but BV and HM (uninhabited, absent from tzdata's zone.tab) —
// a short "popular 60" list here was the only place a country could go
// missing, and Moldova did (Э11). countries.test.ts pins this set to the
// standard and to internal/geo.
//
// Names come from the platform's CLDR (Intl.DisplayNames) in the UI's locale;
// search matches the code, the Russian and the English name, and a few names
// people type that CLDR does not use (UK, USA, Turkey …).

export const ISO_COUNTRY_CODES = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
] as const;

/** Names people search by that CLDR spells otherwise. */
const ALIASES: Partial<Record<string, string[]>> = {
  AE: ["UAE", "ОАЭ", "Эмираты"],
  CI: ["Ivory Coast", "Кот-д'Ивуар"],
  CZ: ["Czech Republic", "Чехия"],
  GB: ["UK", "Great Britain", "England", "Британия", "Англия"],
  KR: ["Korea", "Корея"],
  MK: ["Macedonia", "Македония"],
  NL: ["Holland", "Голландия"],
  SZ: ["Swaziland", "Свазиленд"],
  TR: ["Turkey", "Türkiye", "Турция"],
  US: ["USA", "America", "США", "Америка"],
};

export interface CountryEntry {
  code: string;
  /** The name in the UI's locale. */
  name: string;
  flag: string;
  /** Lower-cased, accent-free code + ru + en names + aliases. */
  haystack: string;
}

export function flagEmoji(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "";
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + (c.charCodeAt(0) - 65)));
}

function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").replace(/['’ʼ`]/g, "").toLowerCase();
}

function displayNames(locale: string): (code: string) => string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region", fallback: "code" });
    return (code) => dn.of(code) ?? code;
  } catch {
    return (code) => code;
  }
}

const catalogs = new Map<string, CountryEntry[]>();

/** Every ISO country, named and sorted for `locale` ("ru" | "en"). */
export function countryCatalog(locale: string): CountryEntry[] {
  const cached = catalogs.get(locale);
  if (cached) return cached;
  const ru = displayNames("ru");
  const en = displayNames("en");
  const own = locale === "ru" ? ru : locale === "en" ? en : displayNames(locale);
  const collator = new Intl.Collator(locale);
  const list = ISO_COUNTRY_CODES.map((code) => ({
    code,
    name: own(code),
    flag: flagEmoji(code),
    haystack: fold([code, ru(code), en(code), ...(ALIASES[code] ?? [])].join(" ")),
  })).sort((a, b) => collator.compare(a.name, b.name));
  catalogs.set(locale, list);
  return list;
}

/** Every ISO country as `TagInput mode="values"` offers it: the code, named «PL · Польша». */
export function countrySuggestions(locale: string): { value: string; label: string }[] {
  return countryCatalog(locale).map((c) => ({ value: c.code, label: `${c.code} · ${c.name}` }));
}

/**
 * The countries a search query names: an exact code first, then names that
 * start with the query, then the rest that contain it. Empty query = all.
 */
export function searchCountries(catalog: CountryEntry[], query: string): CountryEntry[] {
  const q = fold(query.trim());
  if (!q) return catalog;
  const rank = (c: CountryEntry): number => {
    if (c.code.toLowerCase() === q) return 0;
    if (c.haystack.split(" ").some((w) => w.startsWith(q)) || fold(c.name).startsWith(q)) return 1;
    return c.haystack.includes(q) ? 2 : -1;
  };
  return catalog
    .map((c) => [c, rank(c)] as const)
    .filter(([, r]) => r >= 0)
    .sort((a, b) => a[1] - b[1])
    .map(([c]) => c);
}
